import { useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DeleteOutline from '@mui/icons-material/DeleteOutlined';
import FolderOpenOutlined from '@mui/icons-material/FolderOpenOutlined';
import { KeyValueList } from '../../components/KeyValueList';
import { useNotice } from '../../components/NoticeProvider';
import { StatusChip } from '../../components/StatusChip';
import { shortDate } from '../../lib/dates';
import { FINDER_NOTICE, useCanOpenFinder } from '../../lib/finder';
import { FileIcon } from './FileIcon';
import { DOC_STATUS_TONE, fileName, type DocRecord } from './sample';

type Props = {
  r: DocRecord;
  onClose: () => void; /** leave out when the records are written elsewhere (the vault) and cannot be removed here */
  onDelete?: (id: string) => void;
};

/** One document record. Deleting removes the RECORD only: the file on the Mac is never touched. */
export function DocumentDetail({ r, onClose, onDelete }: Props) {
  const notice = useNotice();
  const canFinder = useCanOpenFinder();
  const [confirm, setConfirm] = useState(false);
  return (
    <Stack spacing={2} sx={{ p: { xs: 1.5, sm: 2.5 }, pt: 'max(16px, env(safe-area-inset-top))' }}>
      <Stack direction="row" spacing={1.5} sx={{ alignItems: 'center' }}>
        <FileIcon ext={r.ext} />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="h6" sx={{ overflowWrap: 'anywhere' }}>
            {fileName(r)}
          </Typography>
          <StatusChip label={r.status} tone={DOC_STATUS_TONE[r.status]} />
        </Box>
        <IconButton aria-label="Close" onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </Stack>

      <KeyValueList
        rows={[
          { label: 'Shipment', value: r.srb || 'Not linked yet' },
          { label: 'Type', value: r.type },
          { label: 'Source', value: r.source },
          { label: 'Date', value: shortDate(r.date) },
          { label: 'File in Finder', value: r.path || 'No file yet' },
          { label: 'Notes', value: r.notes || '–' },
        ]}
      />
      <Typography variant="caption" color="text.secondary">
        This is only a record. The file itself stays in Finder on your Mac.
      </Typography>

      <Stack direction="row" spacing={1}>
        {canFinder && (
          <Button
            fullWidth
            variant="outlined"
            startIcon={<FolderOpenOutlined />}
            disabled={!r.path}
            onClick={() => notice(FINDER_NOTICE, 'info')}
          >
            Open in Finder
          </Button>
        )}
        {onDelete && (
          <Button
            fullWidth
            variant="outlined"
            color="error"
            startIcon={<DeleteOutline />}
            onClick={() => setConfirm(true)}
          >
            Delete record
          </Button>
        )}
      </Stack>

      <Dialog open={confirm} onClose={() => setConfirm(false)}>
        <DialogTitle>Delete this record?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Only the record in SERB is removed. The file on your Mac is not touched, moved or deleted.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirm(false)}>Cancel</Button>
          <Button
            color="error"
            onClick={() => {
              setConfirm(false);
              onDelete?.(r.id);
              notice('Record deleted. The file on your Mac was not touched.', 'success');
            }}
          >
            Delete record
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}
